import {forwardRef} from 'react';

import {IconButton, styled} from '@mui/material';
import Box from '@mui/material/Box';

import {MinusIcon} from 'assets/icons/minusIcon';
import {PlusIcon} from 'assets/icons/plusIcon';
import {TrashIcon} from 'assets/icons/trashIcon';

import {FloatingQuantityPickerProps} from './types/floating-quantity-picker.type';
import {Size} from './types/use-floating-quantity-picker.type';

const PickerRoot = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'size'
})<{size?: Size}>(({theme}) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: theme.spacing(1),
  borderRadius: theme.spacing(3),
  backgroundColor: theme.palette.common.white,
  boxShadow: `${theme.spacing(0)} ${theme.spacing(0)} ${theme.spacing(4)} ${theme.spacing(0)} #0000001F`,
  width: '100%',
  height: theme.spacing(4),
  padding: theme.spacing(1)
}));

const IconButtonStyled = styled(IconButton, {
  shouldForwardProp: (prop) => prop !== 'variant'
})<{variant?: Size}>(({variant = 'medium', theme}) => ({
  width: theme.spacing(2.5),
  height: theme.spacing(2.5),
  fontSize: theme.spacing(2),
  padding: 0,
  minWidth: 0,
  borderRadius: '50%',
  ...(variant === 'large' && {fontSize: theme.spacing(2.25)}),
  '&:disabled': {
    opacity: 0.3,
    cursor: 'not-allowed'
  }
}));

const CountText = styled('span', {
  shouldForwardProp: (prop) => prop !== 'size'
})<{size?: Size}>(({theme, size = 'medium'}) => ({
  fontWeight: theme.typography.fontWeightMedium,
  textAlign: 'center',
  color: '#18181B',
  ...(size === 'small' && {
    fontSize: theme.typography.body2.fontSize,
    lineHeight: theme.typography.body2.lineHeight
  }),
  ...(size === 'medium' && {
    fontSize: theme.typography.body1.fontSize,
    lineHeight: theme.typography.body1.lineHeight
  }),
  ...(size === 'large' && {
    fontSize: theme.typography.h6.fontSize,
    lineHeight: theme.typography.h6.lineHeight
  })
}));

const FloatingQuantityPicker = forwardRef<HTMLDivElement, FloatingQuantityPickerProps>((props, ref) => {
  const {
    count,
    handleDelete,
    handleDecrement,
    handleDecrementMouseDown,
    handleMouseUp,
    handleDecrementMouseLeave,
    handleContextMenu,
    handleIncrement,
    handleIncrementMouseDown,
    handleIncrementMouseLeave,
    preventDefault,
    min = 1,
    max,
    size = 'medium',
    ...otherProps
  } = props;

  const minQuantity = count === min;
  const maxQuantity = max !== undefined && count >= max;

  return (
    <PickerRoot ref={ref} {...otherProps} size={size} onClick={preventDefault} className="quantity-picker-container">
      <IconButtonStyled
        onClick={handleDecrement}
        onPointerDown={minQuantity ? handleDelete : handleDecrementMouseDown}
        onPointerUp={handleMouseUp}
        onPointerLeave={handleDecrementMouseLeave}
        onContextMenu={handleContextMenu}
        variant={size}
      >
        {minQuantity ? <TrashIcon /> : <MinusIcon />}
      </IconButtonStyled>

      <CountText size={size}>{count}</CountText>

      <IconButtonStyled
        variant={size}
        onClick={handleIncrement}
        onPointerDown={handleIncrementMouseDown}
        onPointerUp={handleMouseUp}
        onPointerLeave={handleIncrementMouseLeave}
        onContextMenu={handleContextMenu}
        disabled={maxQuantity}
      >
        <PlusIcon />
      </IconButtonStyled>
    </PickerRoot>
  );
});

FloatingQuantityPicker.displayName = 'MakroUI.FloatingQuantityPicker';

export default FloatingQuantityPicker;
